import { ItemInstance, TreeInstance } from '@headless-tree/core';
import React, { useRef } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import {
  InternalTreeItem,
  useDragPreview,
} from 'shared/components/ui/virtualized-tree-list';
import { Task } from 'shared/types/task';

import { useContextMenu } from '../../context-menu-context';
import * as Styled from './tree-task-card.style';

export type TreeTaskCardProps = React.ComponentProps<typeof TaskCard> & {
  tree: TreeInstance<InternalTreeItem>;
  item: ItemInstance<InternalTreeItem>;
  tasks: Task[];
  isMultiSelectModifierPressed?: boolean;
};

export const TreeTaskCard: React.FC<TreeTaskCardProps> = ({
  tree,
  item,
  tasks,
  hideContextMenu,
  isMultiSelectModifierPressed,
  ...taskCardProps
}) => {
  const _isMultiSelected =
    hideContextMenu || tree.getSelectedItems().length > 1;
  const selectedTaskItems = useRef<ItemInstance<InternalTreeItem>[]>([]);
  const selectedTasks = useRef<Task[]>([]);

  const isPreview = useDragPreview();

  // connection with virtualized-tree-list
  const { open } = useContextMenu();

  const handleContextMenu = (e: React.MouseEvent) => {
    // stop the browser's default context menu
    e.preventDefault();
    e.stopPropagation();

    // create a task-map for easy access and prevent loops in loops
    const tasksMap = new Map(tasks.map((task) => [task.id, task]));

    // get all selected tasks
    selectedTaskItems.current = [...tree.getSelectedItems()];
    selectedTasks.current = selectedTaskItems.current
      .map((item) => tasksMap.get(item.getItemData().sourceId))
      .filter(Boolean) as Task[];

    // select item if not selected
    if (!item.isSelected()) {
      tree.setSelectedItems([item.getId()]);
      selectedTaskItems.current.push(item);
      const task = tasksMap.get(item.getItemData().sourceId);
      task && selectedTasks.current.push(task);
    }

    // get the selected items combined
    open(
      { left: e.clientX, top: e.clientY },
      {
        tasks: selectedTasks.current,
      },
    );
  };

  return (
    <Styled.TreeItemContainer onContextMenu={handleContextMenu}>
      <TaskCard
        {...taskCardProps}
        isMultiSelectable
        hideContextMenu={_isMultiSelected || isPreview}
        isSelected={isPreview ? false : item.isSelected()}
        preventInteraction={isMultiSelectModifierPressed}
      />
    </Styled.TreeItemContainer>
  );
};
